import { useState, useEffect, useRef, type CSSProperties } from "react";
import { DayPicker, type DateRange } from "react-day-picker";
import { es } from "react-day-picker/locale";
import "react-day-picker/style.css";
import {
  Calendar,
  Users,
  TrendingUp,
  BookOpen,
  TrendingDown,
  Loader2,
  UtensilsCrossed,
  Coffee,
  Sunrise,
  Moon,
  AlertCircle,
  CheckCircle2,
  UserCheck,
  ArrowRight,
  CalendarDays,
  UserPlus,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { useLocation } from "../contexts/LocationContext";
import { Link, useNavigate } from "react-router-dom";
import SetupTasksCard from "../components/SetupTasksCard";
import SetupWizardCard from "../components/SetupWizardCard";
import { useSetupWizard } from "../contexts/SetupWizardContext";
import { useAuth } from "../contexts/AuthContext";
import { useSetupTasks } from "../hooks/useSetupTasks";
import { useGlobalToast } from "../contexts/ToastContext";
import { getSourceConfig } from "../lib/reservationSources";

interface ShiftData {
  name: string;
  code: string;
  startTime?: string;
  endTime?: string;
  reservations: number;
  pax: number;
}

interface StatusData {
  status: string;
  count: number;
  pax: number;
}

interface UpcomingReservation {
  id: string;
  time: string;
  shift: string;
  partySize: number;
  status: string;
  notes: string | null;
  customer: {
    id: string;
    firstName: string;
    lastName: string | null;
    phone: string | null;
  };
  table: { id: string; name: string } | null;
  tableCombination: {
    id: string;
    name: string;
    tables: { table: { name: string } }[];
  } | null;
  // Solo en modo citas (bookingMode = "appointments"): no hay mesa, sino
  // especialista + servicio (el endpoint de citas solo manda `name`).
  specialist?: { name: string } | null;
  service?: { name: string } | null;
}

interface RecentReservation {
  id: string;
  date: string;
  time: string;
  partySize: number;
  status: string;
  source: string;
  createdAt: string;
  customer: {
    firstName: string;
    lastName: string | null;
  };
}

interface WeekDistribution {
  day: string;
  count: number;
  pax: number;
}

interface DashboardStats {
  today: {
    date: string;
    reservations: number;
    pax: number;
    byShift: ShiftData[];
    byStatus: StatusData[];
    pending: number;
    firstPendingDate: string | null;
  };
  week: {
    start: string;
    end: string;
    reservations: number;
    pax: number;
    change: number;
    distribution: WeekDistribution[];
  };
  month: {
    name: string;
    reservations: number;
    pax: number;
    change: number;
    noShows: number;
    completed: number;
  };
  totals: {
    reservations: number;
    customers: number;
  };
  upcoming: UpcomingReservation[];
  recentCreated: RecentReservation[];
}

/**
 * Consulta por FECHAS (14-sep-2026, petición de Luis): totales exactos de
 * reservas y comensales en un periodo arbitrario. Solo modo mesas — el
 * endpoint agrega reservas (/reservations/stats/range). "Activas" excluye
 * canceladas y no-shows, la MISMA semántica que las tarjetas de hoy/semana.
 */
interface RangeStatsData {
  from: string;
  to: string;
  active: { reservations: number; pax: number };
  total: { reservations: number; pax: number };
  byStatus: Array<{ status: string; count: number; pax: number }>;
}

function toInputDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function RangeStatsCard({ locationId }: { locationId?: string }) {
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const [range, setRange] = useState<{ from: Date; to: Date }>({
    from: new Date(today.getFullYear(), today.getMonth(), 1),
    to: todayStart,
  });
  // Borrador del calendario: permite el estado intermedio "solo desde" sin
  // disparar consultas; al completar el rango se aplica y se cierra.
  const [draft, setDraft] = useState<DateRange | undefined>(undefined);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<RangeStatsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = toInputDate(range.from);
  const to = toInputDate(range.to);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const timer = setTimeout(() => {
      apiClient
        .get<{ success: true; data: RangeStatsData }>("/reservations/stats/range", {
          params: { from, to, ...(locationId ? { locationId } : {}) },
        })
        .then((res) => {
          if (!cancelled) setData(res.data.data);
        })
        .catch(() => {
          if (!cancelled) setError("No se pudo calcular el periodo");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [from, to, locationId]);

  // Cerrar el calendario al tocar fuera.
  useEffect(() => {
    if (!pickerOpen) return;
    const onDown = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [pickerOpen]);

  // Atajos fijos (petición de Luis, 14-sep-2026). Se calculan al pulsar,
  // siempre relativos a hoy.
  const addDays = (d: Date, days: number) => {
    const copy = new Date(d);
    copy.setDate(copy.getDate() + days);
    return copy;
  };
  const dayOfWeek = todayStart.getDay();
  const thisMonday = addDays(todayStart, dayOfWeek === 0 ? -6 : 1 - dayOfWeek);
  const presets: Array<{ key: string; label: string; from: Date; to: Date }> = [
    { key: "today", label: "Hoy", from: todayStart, to: todayStart },
    { key: "7d", label: "Últimos 7 días", from: addDays(todayStart, -6), to: todayStart },
    { key: "30d", label: "Últimos 30 días", from: addDays(todayStart, -29), to: todayStart },
    { key: "lastweek", label: "Semana pasada", from: addDays(thisMonday, -7), to: addDays(thisMonday, -1) },
    {
      key: "month",
      label: "Este mes",
      from: new Date(today.getFullYear(), today.getMonth(), 1),
      to: todayStart,
    },
    {
      key: "lastmonth",
      label: "Mes pasado",
      from: new Date(today.getFullYear(), today.getMonth() - 1, 1),
      to: new Date(today.getFullYear(), today.getMonth(), 0),
    },
    {
      key: "year",
      label: "Este año",
      from: new Date(today.getFullYear(), 0, 1),
      to: todayStart,
    },
  ];
  const activePresetKey = presets.find(
    (preset) => toInputDate(preset.from) === from && toInputDate(preset.to) === to,
  )?.key;

  const rangeLabel = `${range.from.toLocaleDateString("es-ES", { day: "numeric", month: "short" })} – ${range.to.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })}`;

  const cancelledCount = data
    ? data.byStatus
        .filter((b) => ["cancelled", "cancelled_by_restaurant", "no_show"].includes(b.status))
        .reduce((sum, b) => sum + b.count, 0)
    : 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <h2 className="font-semibold text-gray-900">Consulta por fechas</h2>
        <div className="relative" ref={pickerRef}>
          <button
            onClick={() => {
              setDraft({ from: range.from, to: range.to });
              setPickerOpen((open) => !open);
            }}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <CalendarDays className="w-4 h-4 text-gray-400" />
            {rangeLabel}
          </button>
          {pickerOpen && (
            /* Móvil: centrado y fijo (anclado al botón se salía por la
               izquierda); escritorio: anclado bajo el botón como siempre. */
            <div className="fixed left-1/2 -translate-x-1/2 top-[calc(76px+var(--safe-top))] sm:absolute sm:left-auto sm:translate-x-0 sm:top-auto sm:right-0 sm:mt-2 z-30 w-fit bg-white rounded-xl shadow-xl border border-gray-200 p-2">
              <DayPicker
                mode="range"
                numberOfMonths={1}
                locale={es}
                selected={draft}
                defaultMonth={range.from}
                onSelect={(selected) => {
                  setDraft(selected);
                  if (selected?.from && selected?.to) {
                    setRange({ from: selected.from, to: selected.to });
                    setPickerOpen(false);
                  }
                }}
                style={{ "--rdp-accent-color": "#2563eb" } as CSSProperties}
              />
              <p className="text-xs text-gray-400 text-center pb-1">
                Toca el día inicial y el final
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Atajos */}
      <div className="flex gap-1.5 flex-wrap mb-4">
        {presets.map((preset) => (
          <button
            key={preset.key}
            onClick={() => setRange({ from: preset.from, to: preset.to })}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
              activePresetKey === preset.key
                ? "bg-primary-600 text-white border-primary-600"
                : "bg-white text-gray-600 border-gray-200 hover:border-primary-300 hover:text-primary-700"
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>

      {error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : !data && loading ? (
        <p className="text-sm text-gray-400">Calculando...</p>
      ) : data ? (
        <div className={loading ? "opacity-60 transition-opacity" : "transition-opacity"}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Reservas</p>
              <p className="text-2xl md:text-3xl font-bold text-gray-900">
                {data.active.reservations}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Comensales</p>
              <p className="text-2xl md:text-3xl font-bold text-gray-900">{data.active.pax}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 mt-3 flex-wrap">
            {data.byStatus.map((b) => (
              <span
                key={b.status}
                className={`text-xs px-2 py-0.5 rounded-full ${statusColors[b.status] || "bg-gray-100 text-gray-700"}`}
                title={`${b.pax} comensales`}
              >
                {statusLabels[b.status] || b.status}: {b.count}
              </span>
            ))}
          </div>
          {cancelledCount > 0 && (
            <p className="text-xs text-gray-400 mt-2">
              Los totales de arriba no incluyen canceladas ni no-shows ({cancelledCount}) — misma
              regla que las tarjetas de hoy y semana.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

const statusLabels: Record<string, string> = {
  pending: "Pendiente",
  confirmed: "Confirmada",
  double_confirmed: "Doble Confirmada",
  arrived: "Llegó",
  seated: "Sentado",
  completed: "Completada",
  cancelled: "Cancelada",
  cancelled_by_restaurant: "Cancelada (negocio)",
  no_show: "No Show",
};

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-blue-100 text-blue-800",
  double_confirmed: "bg-green-100 text-green-800",
  arrived: "bg-purple-100 text-purple-800",
  seated: "bg-indigo-100 text-indigo-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-800",
  no_show: "bg-gray-100 text-gray-800",
};

export default function Dashboard() {
  const { currentLocation, locationVersion, currentBookingMode } = useLocation();
  const isAppointmentsMode = currentBookingMode === "appointments";
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "owner";
  // La guía rápida de citas solo mientras falte algo IMPRESCINDIBLE para
  // recibir citas (Luis, 21-sep-2026: "Centro Elle ya sabe cómo funciona, no
  // sería necesario que lo tuviese ahí"). Misma query que SetupTasksCard, así
  // que no hay petición extra. Sin datos (staff, endpoint caído): no se pinta.
  const { data: setupTasks } = useSetupTasks(isAdmin && isAppointmentsMode);
  // Mientras el asistente guiado tenga algo pendiente, él es la tarjeta que
  // manda: la de la guía (que solo enlaza a las pantallas) sobraría al lado.
  const { pendiente: configuracionPendiente } = useSetupWizard();
  const TAREAS_IMPRESCINDIBLES_CITAS = ["horarios", "servicios", "profesionales", "compartir_widget"];
  const showAppointmentsGuide =
    isAppointmentsMode &&
    !configuracionPendiente &&
    !!setupTasks &&
    setupTasks.tareas.some((t) => TAREAS_IMPRESCINDIBLES_CITAS.includes(t.id) && !t.hecha);
  // En modo citas, los "turnos" del establecimiento (Comida/Cena) no aplican:
  // mostramos franjas genéricas Mañana/Tarde para que el panel sea útil.
  const remapShiftName = (name: string): string => {
    if (!isAppointmentsMode) return name;
    const n = name.toLowerCase();
    if (n.includes("comid") || n.includes("almuerz") || n.includes("lunch"))
      return "Ma\u00f1ana";
    if (n.includes("cena") || n.includes("dinner") || n.includes("noche"))
      return "Tarde";
    return name;
  };
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { showToast: showGlobalToast } = useGlobalToast();

  useEffect(() => {
    // Bandera "vigente" + AbortController: si el usuario cambia de sede (o de
    // modo) rápido (A→B), la respuesta tardía de A no debe pisar los datos
    // de B ni apagar su loading.
    let active = true;
    const controller = new AbortController();

    const fetchStats = async () => {
      try {
        setLoading(true);
        const params = currentLocation
          ? { locationId: currentLocation.id }
          : {};
        // El backend expone el mismo shape DashboardStats en los dos modos,
        // solo cambia el módulo de origen (reservas de mesa vs. citas).
        const endpoint = isAppointmentsMode
          ? "/appointments/stats/dashboard"
          : "/reservations/stats/dashboard";
        const response = await apiClient.get(endpoint, {
          params,
          signal: controller.signal,
        });
        if (!active) return;
        setStats(response.data.data);
      } catch (error) {
        if (!active || controller.signal.aborted) return;
        console.error("Error fetching dashboard stats:", error);
        // No dejar datos obsoletos (de otra sede/modo) en pantalla sin avisar.
        setStats(null);
        showGlobalToast("No se pudieron cargar las estadísticas", "error");
      } finally {
        if (active) setLoading(false);
      }
    };

    if (currentLocation) {
      fetchStats();
    }

    return () => {
      active = false;
      controller.abort();
    };
  }, [currentLocation, locationVersion, isAppointmentsMode, showGlobalToast]);

  const getShiftIcon = (code: string) => {
    switch (code) {
      case "desayuno":
        return <Sunrise className="w-5 h-5" />;
      case "brunch":
        return <Coffee className="w-5 h-5" />;
      case "comida":
        return <UtensilsCrossed className="w-5 h-5" />;
      case "cena":
        return <Moon className="w-5 h-5" />;
      // Franjas del modo citas (bookingMode = "appointments")
      case "manana":
        return <Sunrise className="w-5 h-5" />;
      case "tarde":
        return <Coffee className="w-5 h-5" />;
      default:
        return <UtensilsCrossed className="w-5 h-5" />;
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-ES", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  const formatTime = (timeStr: string) => {
    return timeStr.slice(0, 5);
  };

  const getTableName = (reservation: UpcomingReservation) => {
    if (isAppointmentsMode) {
      if (reservation.service && reservation.specialist) {
        return `${reservation.service.name} · ${reservation.specialist.name}`;
      }
      if (reservation.service) return reservation.service.name;
      if (reservation.specialist) return reservation.specialist.name;
      return "Sin servicio";
    }
    if (reservation.table) return reservation.table.name;
    if (reservation.tableCombination) {
      return reservation.tableCombination.tables
        .map((t) => t.table.name)
        .join(" + ");
    }
    return "Sin mesa";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-10 w-10 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-500">
        <AlertCircle className="h-12 w-12 mb-4" />
        <p>No se pudieron cargar las estadísticas</p>
      </div>
    );
  }

  // Calculate max for chart (mínimo 1 para no dividir por 0 en el %)
  const maxWeekValue = Math.max(
    ...stats.week.distribution.map((d) => d.count),
    1,
  );

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            ¡Hola! 👋
          </h1>
          <p className="text-gray-500 mt-1">
            {currentLocation ? (
              <>
                Resumen de{" "}
                <span className="font-medium text-gray-700">
                  {currentLocation.name}
                </span>
              </>
            ) : (
              "Tu panel de control"
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={isAppointmentsMode ? "/appointments" : "/reservations"}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
          >
            <Calendar className="w-4 h-4" />
            Ver {isAppointmentsMode ? "Citas" : "Reservas"}
          </Link>
        </div>
      </div>

      {/* El asistente viejo (OnboardingWizard) se retira aquí el 19-sep-2026:
          sus pasos viven ahora en SetupTasksCard, que es UNA sola lista. Dos
          tarjetas de "termina de configurarte" en la misma pantalla eran justo
          el agobio que queriamos evitar. El componente y su endpoint siguen en
          el repo hasta confirmar que nada mas los usa. */}

      {/* Lo que IMPIDE recibir reservas, con el asistente guiado que lo
          rellena paso a paso (27-sep-2026). Va antes que el resumen de tareas:
          sin esto el negocio no funciona; lo otro es presencia. */}
      <SetupWizardCard />

      {/* Perfil del negocio a medias (dirección, foto, página web...) */}
      <SetupTasksCard />

      {/* Citas: los pasos en orden para dejar el negocio recibiendo citas.
          Solo mientras falte algo imprescindible; después queda en el Centro de Ayuda. */}
      {showAppointmentsGuide && (
        <Link
          to="/appointments/guide"
          className="flex items-center gap-4 bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-5 hover:shadow-md transition-shadow"
        >
          <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900">Guía rápida: tu negocio de citas en marcha</p>
            <p className="text-sm text-gray-500 mt-0.5">
              Horario, servicios, profesionales y tu enlace de citas: qué crear y en qué orden.
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
        </Link>
      )}

      {/* Consulta por fechas (solo mesas: agrega reservas) */}
      {!isAppointmentsMode && <RangeStatsCard locationId={currentLocation?.id} />}

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Reservas Hoy */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">
                {isAppointmentsMode ? "Citas Hoy" : "Reservas Hoy"}
              </p>
              <p className="text-2xl md:text-3xl font-bold text-gray-900 mt-1">
                {stats.today.reservations}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {stats.today.pax}{" "}
                {isAppointmentsMode ? "personas" : "comensales"}
              </p>
            </div>
            <div className="p-3 bg-primary-50 rounded-xl">
              <CalendarDays className="text-primary-600 w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Esta Semana */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Esta Semana</p>
              <p className="text-2xl md:text-3xl font-bold text-gray-900 mt-1">
                {stats.week.reservations}
              </p>
              <div className="flex items-center gap-1 mt-1">
                {stats.week.change >= 0 ? (
                  <TrendingUp className="w-4 h-4 text-green-600" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-600" />
                )}
                <span
                  className={`text-sm font-medium ${stats.week.change >= 0 ? "text-green-600" : "text-red-600"}`}
                >
                  {stats.week.change > 0 ? "+" : ""}
                  {stats.week.change}%
                </span>
                <span className="text-xs text-gray-500">vs semana ant.</span>
              </div>
            </div>
            <div className="p-3 bg-blue-50 rounded-xl">
              <TrendingUp className="text-blue-600 w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Este Mes */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 capitalize">
                {stats.month.name}
              </p>
              <p className="text-2xl md:text-3xl font-bold text-gray-900 mt-1">
                {stats.month.reservations}
              </p>
              <div className="flex items-center gap-1 mt-1">
                {stats.month.change >= 0 ? (
                  <TrendingUp className="w-4 h-4 text-green-600" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-600" />
                )}
                <span
                  className={`text-sm font-medium ${stats.month.change >= 0 ? "text-green-600" : "text-red-600"}`}
                >
                  {stats.month.change > 0 ? "+" : ""}
                  {stats.month.change}%
                </span>
                <span className="text-xs text-gray-500">vs mes ant.</span>
              </div>
            </div>
            <div className="p-3 bg-emerald-50 rounded-xl">
              <Calendar className="text-emerald-600 w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Total Clientes */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">
                Clientes Totales
              </p>
              <p className="text-2xl md:text-3xl font-bold text-gray-900 mt-1">
                {stats.totals.customers}
              </p>
              <p className="text-sm text-gray-500 mt-1">en tu base de datos</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-xl">
              <Users className="text-purple-600 w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Alerts Row - only show if there are pending reservations */}
      {stats.today.pending > 0 && (
        <div
          onClick={() => {
            const dateParam = stats.today.firstPendingDate
              ? `&date=${stats.today.firstPendingDate}`
              : "";
            const basePath = isAppointmentsMode
              ? "/appointments"
              : "/reservations";
            navigate(`${basePath}?status=pending${dateParam}`);
          }}
          className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center justify-between cursor-pointer hover:bg-yellow-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="font-medium text-yellow-800">
                {stats.today.pending}{" "}
                {isAppointmentsMode ? "cita" : "reserva"}
                {stats.today.pending !== 1 ? "s" : ""} pendiente
                {stats.today.pending !== 1 ? "s" : ""} de confirmar
              </p>
              <p className="text-sm text-yellow-600">
                {isAppointmentsMode
                  ? "Necesitan confirmación"
                  : "Necesitan asignación de mesa o confirmación"}
              </p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-yellow-600" />
        </div>
      )}

      {/* Today's Shifts + Upcoming */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Shifts breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">
              {isAppointmentsMode ? "Hoy por franjas" : "Hoy por Turnos"}
            </h3>
          </div>
          <div className="p-4 space-y-3">
            {stats.today.byShift.map((shift) => (
              <div
                key={shift.code}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  shift.reservations > 0 ? "bg-gray-50" : "bg-gray-25"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 rounded-lg ${
                      shift.reservations > 0
                        ? "bg-primary-100 text-primary-600"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    {getShiftIcon(shift.code)}
                  </div>
                  <div>
                    <p
                      className={`font-medium ${shift.reservations > 0 ? "text-gray-900" : "text-gray-500"}`}
                    >
                      {remapShiftName(shift.name)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {shift.startTime && shift.endTime
                        ? `${shift.startTime} - ${shift.endTime}`
                        : ""}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p
                    className={`text-lg font-bold ${shift.reservations > 0 ? "text-gray-900" : "text-gray-400"}`}
                  >
                    {shift.reservations}
                  </p>
                  <p className="text-xs text-gray-500">
                    {shift.pax} {isAppointmentsMode ? "" : "pax"}
                    {isAppointmentsMode ? "pers." : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Today */}
        <div className="md:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">
              Próximas {isAppointmentsMode ? "Citas" : "Reservas"} de Hoy
            </h3>
            <Link
              to={isAppointmentsMode ? "/appointments" : "/reservations"}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
            >
              Ver todas <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-gray-100">
            {stats.upcoming.length === 0 ? (
              <div className="p-8 text-center">
                <CheckCircle2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">
                  No hay más {isAppointmentsMode ? "citas" : "reservas"} para
                  hoy
                </p>
              </div>
            ) : (
              stats.upcoming.map((reservation) => (
                <div
                  key={reservation.id}
                  onClick={() =>
                    navigate(
                      `${isAppointmentsMode ? "/appointments" : "/reservations"}?id=${reservation.id}`,
                    )
                  }
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  {/* Móvil-first: la etiqueta comparte línea con el nombre
                      (que trunca con espacio real) y los metadatos envuelven
                      debajo — antes el bloque derecho estrujaba el nombre. */}
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="flex flex-col items-center justify-center bg-primary-50 text-primary-700 rounded-lg px-3 py-2 min-w-[60px] flex-shrink-0">
                      <span className="text-lg font-bold leading-none">
                        {formatTime(reservation.time)}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium text-gray-900 truncate">
                          {reservation.customer.firstName}{" "}
                          {reservation.customer.lastName || ""}
                        </p>
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${statusColors[reservation.status] || "bg-gray-100 text-gray-800"}`}
                        >
                          {statusLabels[reservation.status] || reservation.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-x-2 gap-y-0.5 mt-0.5 flex-wrap">
                        <span className="text-sm text-gray-500 whitespace-nowrap">
                          <Users className="w-3.5 h-3.5 inline mr-1" />
                          {reservation.partySize}{" "}
                          {isAppointmentsMode ? "pers." : "pax"}
                        </span>
                        <span className="text-gray-300">•</span>
                        <span className="text-sm text-gray-500 truncate">
                          {getTableName(reservation)}
                        </span>
                      </div>
                    </div>
                  </div>
                  {reservation.notes && (
                    <p className="mt-2 text-sm text-gray-500 pl-[76px] line-clamp-1">
                      📝 {reservation.notes}
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Week Chart + Recent + Month Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Week distribution chart */}
        <div className="md:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">
              {isAppointmentsMode ? "Citas" : "Reservas"} de la Semana
            </h3>
            <p className="text-sm text-gray-500">
              {stats.week.start && formatDate(stats.week.start)} -{" "}
              {stats.week.end && formatDate(stats.week.end)}
            </p>
          </div>
          <div className="p-5">
            {stats.week.distribution.length === 0 ? (
              <div className="py-12 text-center text-gray-400">
                <CalendarDays className="w-12 h-12 mx-auto mb-3" />
                <p>No hay datos para esta semana</p>
              </div>
            ) : (
              <div className="flex items-end justify-between gap-2 h-48">
                {stats.week.distribution.map((day, index) => {
                  const heightPercent =
                    maxWeekValue > 0 ? (day.count / maxWeekValue) * 100 : 0;
                  const dayDate = new Date(day.day);
                  const dayName = dayDate.toLocaleDateString("es-ES", {
                    weekday: "short",
                  });
                  const dayNum = dayDate.getDate();
                  const isToday = day.day === stats.today.date;

                  return (
                    <div
                      key={index}
                      className="flex-1 flex flex-col items-center"
                    >
                      {/* Number above bar */}
                      <span className="text-xs font-medium text-gray-700 mb-1">
                        {day.count}
                      </span>
                      {/* Bar container with fixed height */}
                      <div className="w-full flex items-end justify-center h-28">
                        <div
                          className={`w-full max-w-[40px] rounded-t-lg transition-all ${
                            isToday ? "bg-primary-500" : "bg-primary-200"
                          }`}
                          style={{
                            height:
                              day.count > 0
                                ? `${Math.max(heightPercent, 10)}%`
                                : "4px",
                          }}
                        />
                      </div>
                      {/* Day label */}
                      <div className="text-center mt-2">
                        <p
                          className={`text-xs font-medium ${isToday ? "text-primary-600" : "text-gray-600"}`}
                        >
                          {dayName}
                        </p>
                        <p
                          className={`text-xs ${isToday ? "text-primary-500" : "text-gray-400"}`}
                        >
                          {dayNum}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Month Stats */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900 capitalize">
              Resumen {stats.month.name}
            </h3>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-gray-600">
                  Total {isAppointmentsMode ? "Citas" : "Reservas"}
                </span>
              </div>
              <span className="text-xl font-bold text-gray-900">
                {stats.month.reservations}
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-50 rounded-lg">
                  <UserCheck className="w-5 h-5 text-emerald-600" />
                </div>
                <span className="text-gray-600">Completadas</span>
              </div>
              <span className="text-xl font-bold text-emerald-600">
                {stats.month.completed}
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-50 rounded-lg">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <span className="text-gray-600">
                  {isAppointmentsMode ? "Personas" : "Comensales"}
                </span>
              </div>
              <span className="text-xl font-bold text-gray-900">
                {stats.month.pax}
              </span>
            </div>
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <span className="text-gray-600">No Shows</span>
              </div>
              <span className="text-xl font-bold text-red-600">
                {stats.month.noShows}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Recently Created */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">
              {isAppointmentsMode
                ? "Últimas Citas Creadas"
                : "Últimas Reservas Creadas"}
            </h3>
            <p className="text-sm text-gray-500">Las más recientes primero</p>
          </div>
          <Link
            to="/customers"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
          >
            <UserPlus className="w-4 h-4" /> Ver Clientes
          </Link>
        </div>
        <div className="divide-y divide-gray-100">
          {stats.recentCreated.length === 0 ? (
            <div className="p-8 text-center">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">
                No hay {isAppointmentsMode ? "citas" : "reservas"} recientes
              </p>
            </div>
          ) : (
            stats.recentCreated.map((reservation) => {
              const sourceConfig = getSourceConfig(reservation.source);
              return (
                <div
                  key={reservation.id}
                  onClick={() =>
                    navigate(
                      `${isAppointmentsMode ? "/appointments" : "/reservations"}?id=${reservation.id}`,
                    )
                  }
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors flex items-center gap-3 sm:gap-4"
                >
                  <div className="flex flex-col items-center justify-center bg-gray-100 text-gray-700 rounded-lg px-2 py-2 min-w-[72px] flex-shrink-0">
                    <span className="text-xs text-gray-500">
                      {formatDate(reservation.date)}
                    </span>
                    <span className="text-sm font-bold">
                      {formatTime(reservation.time)}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-gray-900 truncate">
                        {reservation.customer.firstName}{" "}
                        {reservation.customer.lastName || ""}
                      </p>
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${statusColors[reservation.status] || "bg-gray-100 text-gray-800"}`}
                      >
                        {statusLabels[reservation.status] || reservation.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-x-2 gap-y-0.5 mt-0.5 flex-wrap">
                      <span className="text-sm text-gray-500 whitespace-nowrap">
                        <Users className="w-3.5 h-3.5 inline mr-1" />
                        {reservation.partySize} pax
                      </span>
                      <span className="text-gray-300">•</span>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        vía {sourceConfig.label}
                      </span>
                      <span className="text-gray-300 hidden sm:inline">•</span>
                      <span className="text-xs text-gray-400 w-full sm:w-auto">
                        creada{" "}
                        {new Date(reservation.createdAt).toLocaleDateString(
                          "es-ES",
                          {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
